import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Campo, Selector } from '../componentes/ui/formulario';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { usePermiso } from '../sesion/ProveedorDeSesion';
import { importeParaMirar } from '../lib/numero';
import { aplanarElArbol, etiquetaSangrada, type Rubro } from './Rubros';

interface Lista {
  readonly id: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly monedaCodigo: string;
  readonly esPredeterminada: boolean;
}

interface Proveedor {
  readonly id: string;
  readonly razonSocial: string;
}

interface LineaDeVista {
  readonly varianteId: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly marca: string | null;
  readonly precioAnterior: string | null;
  readonly precioNuevo: string | null;
  readonly costoConvertido: string | null;
  readonly costoVigenteDesde: string | null;
  readonly margenSobreVenta: string | null;
  readonly bajoElCosto: boolean;
  readonly motivo: 'sin_precio_actual' | 'sin_costo' | null;
}

interface VistaPrevia {
  readonly lineas: readonly LineaDeVista[];
  readonly moneda: string;
  readonly cotizacionesUsadas: Record<string, string>;
}

type ClaseDeRedondeo = 'decimales' | 'multiplo' | 'terminacion';

/**
 * El margen, con un decimal y su signo de porcentaje.
 *
 * Está afuera del JSX porque el lint —regla 3— rechaza literales adentro, y hace bien: el `%` es
 * de los pocos símbolos que se escriben igual en los tres idiomas, pero la regla no puede saberlo
 * y aflojarla por un caso es aflojarla para todos.
 */
function margenParaMirar(margen: string): string {
  return `${Number(margen).toFixed(1)}%`;
}

/**
 * La cotización, sin los ceros que sobran.
 *
 * Se guarda con ocho decimales porque el camino inverso —de pesos a dólares— los necesita, pero
 * «1480,00000000» en pantalla no informa más que «1480»: son precisión interna, no información.
 */
function cotizacionParaMirar(valor: string, idioma: string): string {
  return importeParaMirar(valor.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, ''), idioma);
}

/** La coma decimal de acá se manda como punto: el servidor no adivina idiomas. */
function normalizar(texto: string): string {
  return texto.trim().replace(',', '.');
}

/**
 * Recalcular precios.
 *
 * ## Por qué es una pantalla aparte de Precios
 *
 * Porque es otra operación. En Precios se carga un precio, de a uno, mirando el artículo. Acá se
 * cambian trescientos de una vez, mirando un filtro, y **lo que importa es lo que va a pasar antes
 * de que pase**. Mezclarlas haría que la grilla de carga tuviera un botón que cambia trescientas
 * filas que no se están viendo.
 *
 * ## Las dos cosas que la pantalla tiene que dejar claras
 *
 * 1. **Que la vista previa no escribe nada.** Se puede probar un filtro, mirarlo, cambiar el
 *    porcentaje y volver a mirar, sin consecuencias.
 * 2. **Que utilidad y margen no son el mismo número.** Se muestran los dos, uno al lado del otro,
 *    porque es la confusión más cara del dominio y no se resuelve con una aclaración chiquita.
 */
export function RecalcularPrecios(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();
  const puedeAplicar = usePermiso('PRECIOS.AUMENTO.APLICAR');

  const [listaId, setListaId] = useState('');
  const [base, setBase] = useState<'costo_de_reposicion' | 'precio_actual'>('costo_de_reposicion');
  const [porcentaje, setPorcentaje] = useState('40');
  const [claseDeRedondeo, setClaseDeRedondeo] = useState<ClaseDeRedondeo>('decimales');
  const [valorDeRedondeo, setValorDeRedondeo] = useState('2');
  const [proveedorId, setProveedorId] = useState('');
  const [rubroId, setRubroId] = useState('');
  const [marca, setMarca] = useState('');
  // Vacío es «ahora». Con una fecha futura cada precio queda programado y entra solo ese día (E36).
  const [aplicarEl, setAplicarEl] = useState('');
  const [vista, setVista] = useState<VistaPrevia | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const listas = useQuery({
    queryKey: ['listas-de-precios'],
    queryFn: () => pedir<Lista[]>('/listas-de-precios'),
  });
  const rubros = useQuery({
    queryKey: ['categorias'],
    queryFn: () => pedir<Rubro[]>('/categorias'),
  });
  const proveedores = useQuery({
    queryKey: ['terceros', 'proveedores'],
    queryFn: () => pedir<Proveedor[]>('/terceros?soloProveedores=true'),
  });

  const listaActiva =
    listas.data?.find((lista) => lista.id === listaId) ??
    listas.data?.find((lista) => lista.esPredeterminada) ??
    listas.data?.[0];

  function cuerpoDelPedido(): Record<string, unknown> {
    return {
      listaId: listaActiva?.id ?? '',
      base,
      porcentaje: normalizar(porcentaje),
      redondeo:
        claseDeRedondeo === 'decimales'
          ? { clase: 'decimales', decimales: Number(valorDeRedondeo) }
          : claseDeRedondeo === 'multiplo'
            ? { clase: 'multiplo', multiplo: normalizar(valorDeRedondeo) }
            : { clase: 'terminacion', terminacion: normalizar(valorDeRedondeo) },
      filtro: {
        ...(proveedorId === '' ? {} : { proveedorId }),
        ...(rubroId === '' ? {} : { rubroId }),
        ...(marca.trim() === '' ? {} : { marca: marca.trim() }),
      },
    };
  }

  const previsualizar = useMutation({
    mutationFn: () =>
      pedir<VistaPrevia>('/precios/recalculo/vista-previa', {
        method: 'POST',
        cuerpo: cuerpoDelPedido(),
      }),
    onSuccess: (respuesta) => {
      setVista(respuesta);
      setError(null);
      setAviso(null);
    },
    onError: (fallo: unknown) => {
      setVista(null);
      setAviso(null);
      setError(textoDeError(fallo));
    },
  });

  const aplicar = useMutation({
    mutationFn: () =>
      pedir<{ actualizados: number; sinCalcular: number; programados: number }>(
        '/precios/recalculo',
        {
          method: 'POST',
          cuerpo: { ...cuerpoDelPedido(), ...(aplicarEl === '' ? {} : { aplicarEl }) },
        },
      ),
    onSuccess: async (respuesta) => {
      setVista(null);
      setError(null);
      setAviso(
        respuesta.programados > 0
          ? t('recalculo.programado', { count: respuesta.programados, fecha: aplicarEl })
          : t('recalculo.aplicado', { count: respuesta.actualizados }),
      );
      await cola.invalidateQueries({ queryKey: ['precios'] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  // Cambiar cualquier parámetro invalida lo que se está mirando: una vista previa vieja al lado de
  // un porcentaje nuevo es peor que no tener vista previa.
  function alCambiar<T>(poner: (valor: T) => void): (valor: T) => void {
    return (valor) => {
      poner(valor);
      setVista(null);
      setAviso(null);
    };
  }

  const idioma = i18n.language;
  const calculables = (vista?.lineas ?? []).filter((linea) => linea.motivo === null);
  const cambian = calculables.filter((linea) => linea.precioAnterior !== linea.precioNuevo);
  const sinCalcular = (vista?.lineas ?? []).length - calculables.length;

  const columnas: Columna<LineaDeVista>[] = [
    {
      clave: 'codigo',
      titulo: t('recalculo.codigo'),
      contenido: (fila) => <span className="numero">{fila.codigo}</span>,
    },
    {
      clave: 'nombre',
      titulo: t('recalculo.articulo'),
      contenido: (fila) => (
        <>
          {fila.nombre}
          {fila.marca === null ? null : (
            <span className="block text-apoyo text-texto-tenue">{fila.marca}</span>
          )}
        </>
      ),
    },
    {
      clave: 'costo',
      titulo: t('recalculo.costo'),
      // Con la fecha debajo, siempre. **Un costo sin fecha no se puede juzgar**: un precio
      // calculado sobre un costo de marzo es un precio de marzo con cara de nuevo.
      contenido: (fila) =>
        fila.costoConvertido === null ? (
          <span className="text-texto-tenue">—</span>
        ) : (
          <>
            <span className="cifra">{importeParaMirar(fila.costoConvertido, idioma)}</span>
            {fila.costoVigenteDesde === null ? null : (
              <span className="block text-apoyo text-texto-tenue">
                {fila.costoVigenteDesde.slice(0, 10)}
              </span>
            )}
          </>
        ),
    },
    {
      clave: 'antes',
      titulo: t('recalculo.precioAnterior'),
      contenido: (fila) =>
        fila.precioAnterior === null ? (
          <span className="text-texto-tenue">{t('recalculo.sinPrecio')}</span>
        ) : (
          <span className="cifra">{importeParaMirar(fila.precioAnterior, idioma)}</span>
        ),
    },
    {
      clave: 'despues',
      titulo: t('recalculo.precioNuevo'),
      contenido: (fila) =>
        fila.precioNuevo === null ? (
          <Insignia tono="neutro">
            {fila.motivo === 'sin_costo' ? t('recalculo.sinCosto') : t('recalculo.sinPrecioBase')}
          </Insignia>
        ) : (
          <span className="cifra font-medium">{importeParaMirar(fila.precioNuevo, idioma)}</span>
        ),
    },
    {
      clave: 'margen',
      titulo: t('recalculo.margenSobreVenta'),
      contenido: (fila) =>
        fila.margenSobreVenta === null ? (
          <span className="text-texto-tenue">—</span>
        ) : (
          <span className={`cifra ${fila.bajoElCosto ? 'text-mal' : ''}`}>
            {margenParaMirar(fila.margenSobreVenta)}
          </span>
        ),
    },
  ];

  if (listas.isPending) return <Cargando />;
  if (listas.isError) return <Problema error={listas.error} />;

  return (
    <>
      <Encabezado titulo={t('recalculo.titulo')} descripcion={t('recalculo.descripcion')} />

      <Tarjeta>
        <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Selector
            etiqueta={t('recalculo.lista')}
            value={listaActiva?.id ?? ''}
            onChange={(evento) => {
              alCambiar(setListaId)(evento.target.value);
            }}
          >
            {(listas.data ?? []).map((lista) => (
              <option key={lista.id} value={lista.id}>
                {lista.nombre}
              </option>
            ))}
          </Selector>

          <Selector
            etiqueta={t('recalculo.base')}
            value={base}
            ayuda={
              base === 'costo_de_reposicion'
                ? t('recalculo.ayudaCosto')
                : t('recalculo.ayudaPrecio')
            }
            onChange={(evento) => {
              alCambiar(setBase)(
                evento.target.value === 'precio_actual' ? 'precio_actual' : 'costo_de_reposicion',
              );
            }}
          >
            <option value="costo_de_reposicion">{t('recalculo.baseCosto')}</option>
            <option value="precio_actual">{t('recalculo.basePrecio')}</option>
          </Selector>

          <Campo
            etiqueta={
              base === 'costo_de_reposicion' ? t('recalculo.utilidad') : t('recalculo.aumento')
            }
            value={porcentaje}
            inputMode="decimal"
            ayuda={base === 'costo_de_reposicion' ? t('recalculo.ayudaUtilidad') : undefined}
            onChange={(evento) => {
              alCambiar(setPorcentaje)(evento.target.value);
            }}
          />

          <div className="flex gap-2">
            <Selector
              etiqueta={t('recalculo.redondeo')}
              value={claseDeRedondeo}
              onChange={(evento) => {
                const elegida = evento.target.value as ClaseDeRedondeo;
                alCambiar(setClaseDeRedondeo)(elegida);
                // Cada clase tiene su valor razonable: dejar el «2» de decimales como múltiplo
                // redondearía todo a dos pesos.
                setValorDeRedondeo(
                  elegida === 'decimales' ? '2' : elegida === 'multiplo' ? '100' : '900',
                );
              }}
            >
              <option value="decimales">{t('recalculo.redondeoDecimales')}</option>
              <option value="multiplo">{t('recalculo.redondeoMultiplo')}</option>
              <option value="terminacion">{t('recalculo.redondeoTerminacion')}</option>
            </Selector>
            <Campo
              etiqueta={t('recalculo.valor')}
              value={valorDeRedondeo}
              inputMode="decimal"
              onChange={(evento) => {
                alCambiar(setValorDeRedondeo)(evento.target.value);
              }}
            />
          </div>
        </div>

        <p className="mb-2 text-apoyo font-semibold uppercase tracking-wider text-texto-tenue">
          {t('recalculo.aQuienesAlcanza')}
        </p>
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          <Selector
            etiqueta={t('recalculo.proveedor')}
            value={proveedorId}
            ayuda={t('recalculo.ayudaProveedor')}
            onChange={(evento) => {
              alCambiar(setProveedorId)(evento.target.value);
            }}
          >
            <option value="">{t('recalculo.todosLosProveedores')}</option>
            {(proveedores.data ?? []).map((proveedor) => (
              <option key={proveedor.id} value={proveedor.id}>
                {proveedor.razonSocial}
              </option>
            ))}
          </Selector>
          <Selector
            etiqueta={t('recalculo.rubro')}
            value={rubroId}
            onChange={(evento) => {
              alCambiar(setRubroId)(evento.target.value);
            }}
          >
            <option value="">{t('recalculo.todosLosRubros')}</option>
            {aplanarElArbol(rubros.data ?? []).map((rubro) => (
              <option key={rubro.id} value={rubro.id}>
                {etiquetaSangrada(rubro.nombre, rubro.profundidad)}
              </option>
            ))}
          </Selector>
          <Campo
            etiqueta={t('recalculo.marca')}
            value={marca}
            onChange={(evento) => {
              alCambiar(setMarca)(evento.target.value);
            }}
          />
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <Campo
            etiqueta={t('recalculo.aplicarEl')}
            type="date"
            value={aplicarEl}
            ayuda={t('recalculo.ayudaAplicarEl')}
            onChange={(evento) => {
              setAplicarEl(evento.target.value);
            }}
          />
          <Boton
            cargando={previsualizar.isPending}
            onClick={() => {
              previsualizar.mutate();
            }}
          >
            {t('recalculo.verQuePasaria')}
          </Boton>
          {/* Aplicar sólo después de mirar. No es una traba burocrática: la vista previa es lo
              único que separa «subí 40%» de «subí 40% sin querer a toda la lista». */}
          {puedeAplicar ? (
            <Boton
              tono="principal"
              cargando={aplicar.isPending}
              disabled={vista === null || cambian.length === 0}
              onClick={() => {
                aplicar.mutate();
              }}
            >
              {t('recalculo.aplicar', { count: cambian.length })}
            </Boton>
          ) : null}
          <span className="text-apoyo text-texto-tenue">{t('recalculo.noEscribeNada')}</span>
        </div>

        {error === null ? null : (
          <p role="alert" className="mt-3 text-apoyo text-mal">
            {error}
          </p>
        )}
        {aviso === null ? null : (
          <p role="status" className="mt-3 text-apoyo text-bien">
            {aviso}
          </p>
        )}
      </Tarjeta>

      {vista === null ? null : (
        <Tarjeta>
          <div className="mb-3 flex flex-wrap gap-4 text-cuerpo">
            <span>
              {t('recalculo.cambian')}: <span className="cifra font-medium">{cambian.length}</span>
            </span>
            {sinCalcular === 0 ? null : (
              <span className="text-texto-tenue">
                {t('recalculo.quedanAfuera', { count: sinCalcular })}
              </span>
            )}
            {Object.entries(vista.cotizacionesUsadas).map(([moneda, valor]) => (
              <span key={moneda} className="text-texto-tenue">
                {t('recalculo.cotizacionUsada', {
                  moneda,
                  valor: cotizacionParaMirar(valor, idioma),
                })}
              </span>
            ))}
          </div>

          <Tabla
            columnas={columnas}
            filas={vista.lineas}
            claveDeFila={(fila) => fila.varianteId}
            vacio={
              <Vacio
                titulo={t('recalculo.ningunaAlcanzada')}
                descripcion={t('recalculo.aflojaElFiltro')}
              />
            }
          />
        </Tarjeta>
      )}
    </>
  );
}
